import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import CloseIcon from '@material-ui/icons/Close';
import DoneOutlineIcon from '@material-ui/icons/DoneOutline';
import { Navigation } from '../../containers';
import { getAllPicksOneWeek } from '../../services';
import { playoffWeekNames } from '../../utils';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  table: {
    minWidth: 0,
  },
}));

const ViewAllPicksPage = () => {
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [pageName, setPageName] = useState('View Picks');
  const { weekNumber } = useParams();

  useEffect(() => {
    if (weekNumber !== undefined) {
      const loadPicksData = async (weekNumber) => {
        const picksData = await getAllPicksOneWeek(weekNumber);
        setDataLoading(false);
        // console.log('-- standingsData --', standingsData);
        if (picksData.games && picksData.users) {
          const rawGames = picksData.games;
          rawGames.sort((a, b) => {
            return a.gameDateTime - b.gameDateTime;
          });
          const rawUsers = picksData.users;
          rawUsers.sort((a, b) => {
            return a.user.username.localeCompare(b.user.username);
          });
          setGames(rawGames);
          setUsers(rawUsers);
        }
      }

      loadPicksData(weekNumber);
      const name = playoffWeekNames.find(week => week.weekNumber === parseInt(weekNumber));
      setPageName(name === undefined ? `Picks for Week ${weekNumber}` : `Picks for ${name.weekName}`); 
    }
  }, [weekNumber]);

  return (
    <div>
      <Navigation pageName={`${pageName}`}>
        {dataLoading && (
          <CircularProgress />
        )}
        {PicksTable(users, games)}
      </Navigation>
    </div>
  );
}

export default ViewAllPicksPage;

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#3f51b5',
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const PicksTable = (users, games) => {
  const classes = useStyles();
  if (games.length > 0) {
    return (
      <>
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align='center' />
                {games.map((row) => {
                  let headerHomeBadge = null;
                  let headerVisitingBadge = null;
                  if (row.winningTeamId !== null) {
                    if (row.winningTeamId === row.homeTeam.teamId) {
                      headerHomeBadge = <DoneOutlineIcon style={{ color: '#51B53F', width: 20, height: 20 }} />;
                      headerVisitingBadge = <CloseIcon style={{ color: '#FF0000', width: 25, height: 25 }} />;
                    } else {
                      headerVisitingBadge = <DoneOutlineIcon style={{ color: '#51B53F', width: 20, height: 20 }} />;
                      headerHomeBadge = <CloseIcon style={{ color: '#FF0000', width: 25, height: 25 }} />;
                    }
                  }
                  return (
                    <>
                      <StyledTableCell align='center'>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          badgeContent={headerVisitingBadge}
                        >
                          <Avatar alt={row.visitingTeam.teamName} src={row.visitingTeam.imageUrl} />
                        </Badge>
                        <Typography style={{ paddingLeft: 5, paddingRight: 5 }} key={`${row.gameId}-at`}>@</Typography>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          badgeContent={headerHomeBadge}
                        >
                          <Avatar alt={row.homeTeam.teamName} src={row.homeTeam.imageUrl} />
                        </Badge>
                      </StyledTableCell>
                    </>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((row) => (
                <StyledTableRow key={row.userId}>
                  <StyledTableCell align='center'>
                    <Typography>{row.user.username}</Typography>
                  </StyledTableCell>
                  {games.map((game) => {
                    const pick = row.picks.find((pick) => pick.gameId === game.gameId);
                    if (pick) {
                      const pickedTeam = pick.pickedTeamId;
                      const pickedTeamLogo = pickedTeam === game.visitingTeam.teamId ? game.visitingTeam.imageUrl : game.homeTeam.imageUrl;
                      const pickedTeamName = pickedTeam === game.visitingTeam.teamId ? game.visitingTeam.teamName : game.homeTeam.teamName;
                      let badgeContent = null;
                      if (pickedTeam === game.winningTeamId) {
                        badgeContent = <DoneOutlineIcon style={{ color: '#51B53F', width: 20, height: 20 }} />;
                      } else if (game.winningTeamId !== null) {
                        badgeContent = <CloseIcon style={{ color: '#FF0000', width: 25, height: 25 }} />;
                      }
                      return (
                        <StyledTableCell align='center'>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            badgeContent={badgeContent}
                          >
                            <Avatar alt={pickedTeamName} src={pickedTeamLogo} />
                          </Badge>
                        </StyledTableCell>
                      );
                    } else {
                      return (
                        <StyledTableCell align='center' />
                      )
                    }
                  })}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  } else {
    return null;
  };
}
